/**
 * FeeReconciliationPanel — iter197.5.
 *
 * Inline panel rendered inside the Sessions tab when the segmented control
 * is set to "📊 Reconcile". Three sections:
 *   • Summary tiles — sessions / expected / collected / gap.
 *   • Per-session table — date, title, expected vs collected, gap, ✓/⚠.
 *   • Per-member table — sessions attended / paid / outstanding ₹ + WhatsApp
 *     nudge button + CSV export.
 *
 * Period filter at the top (default = last 60 days, can extend).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw, Loader2, Calendar, Coins, AlertTriangle, CheckCircle2,
  MessageCircle, FileDown, ChevronRight, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

function fmtAmt(n) {
  return `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
      day: 'numeric', month: 'short',
    });
  } catch { return d; }
}

const STATUS_PILLS = {
  fees_charged: { label: '✓ Settled', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' },
  partial: { label: '⚠ Partial', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30' },
  uncharged: { label: '⨯ Uncharged', cls: 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30' },
  complimentary: { label: '🎁 Free', cls: 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30' },
};

function defaultPeriod() {
  const today = new Date();
  const fromDate = new Date();
  fromDate.setDate(fromDate.getDate() - 60);
  const fmt = (d) => d.toISOString().split('T')[0];
  return { from: fmt(fromDate), to: fmt(today) };
}

export default function FeeReconciliationPanel({ groupId, groupName, defaultFee }) {
  const navigate = useNavigate();
  const [period, setPeriod] = useState(defaultPeriod);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/groups/${groupId}/session-fee-reconciliation`, {
        params: { period_from: period.from, period_to: period.to },
      });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load reconciliation');
    } finally {
      setLoading(false);
    }
  }, [groupId, period.from, period.to]);

  useEffect(() => { load(); }, [load]);

  const onWhatsAppNudge = useCallback((member) => {
    if (!member.phone) {
      toast.error(`No phone on file for ${member.name}`);
      return;
    }
    const text =
      `Hi ${member.name}, this is a friendly reminder from ${groupName}: ` +
      `you have ${member.sessions_unpaid_count} session(s) outstanding totalling ` +
      `${fmtAmt(member.outstanding)}. Please settle on SplitEasy when you can. — Treasurer`;
    const phone = (member.phone || '').replace(/\D/g, '');
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [groupName]);

  const onExportCsv = useCallback(() => {
    if (!data) return;
    const rows = [
      ['Section', 'Field 1', 'Field 2', 'Field 3', 'Field 4', 'Field 5'],
      ['__SUMMARY', `Period ${period.from} → ${period.to}`, '', '', '', ''],
      ['Summary', 'sessions_total', data.summary.sessions_total, '', '', ''],
      ['Summary', 'sessions_fees_charged', data.summary.sessions_fees_charged, '', '', ''],
      ['Summary', 'sessions_uncharged', data.summary.sessions_uncharged, '', '', ''],
      ['Summary', 'total_expected', data.summary.total_expected, '', '', ''],
      ['Summary', 'total_collected', data.summary.total_collected, '', '', ''],
      ['Summary', 'total_gap', data.summary.total_gap, '', '', ''],
      ['', '', '', '', '', ''],
      ['__SESSIONS', 'date', 'title', 'expected', 'collected', 'gap'],
      ...data.per_session.map((s) => ['Session', s.date, s.title, s.expected, s.collected, s.gap]),
      ['', '', '', '', '', ''],
      ['__MEMBERS', 'name', 'attended', 'paid', 'unpaid', 'outstanding'],
      ...data.per_member.map((m) => ['Member', m.name, m.sessions_attended, m.sessions_paid, m.sessions_unpaid_count, m.outstanding]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${groupName?.replace(/\s+/g, '_') || 'group'}_fee_reconciliation_${period.from}_${period.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  }, [data, period, groupName]);

  const summary = data?.summary;
  const totalGap = summary?.total_gap || 0;

  return (
    <div className="space-y-4" data-testid="fee-reconcile-panel">
      {/* Period filter */}
      <div className="rounded-2xl border-2 border-border bg-card p-3 flex flex-wrap gap-3 items-end" data-testid="fee-reconcile-period">
        <div className="flex-1 min-w-[120px]">
          <Label htmlFor="recon-from" className="text-xs uppercase tracking-wider font-bold text-muted-foreground">From</Label>
          <Input
            id="recon-from"
            type="date"
            value={period.from}
            onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))}
            className="mt-1 h-9 text-sm"
            data-testid="fee-reconcile-from"
          />
        </div>
        <div className="flex-1 min-w-[120px]">
          <Label htmlFor="recon-to" className="text-xs uppercase tracking-wider font-bold text-muted-foreground">To</Label>
          <Input
            id="recon-to"
            type="date"
            value={period.to}
            onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))}
            className="mt-1 h-9 text-sm"
            data-testid="fee-reconcile-to"
          />
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="fee-reconcile-refresh" className="h-9">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </Button>
        <Button size="sm" variant="outline" onClick={onExportCsv} disabled={!data} data-testid="fee-reconcile-csv" className="h-9 gap-1.5">
          <FileDown className="w-3.5 h-3.5" /> CSV
        </Button>
      </div>

      {error && (
        <div className="rounded-xl border-2 border-destructive bg-destructive/10 px-3 py-2 text-xs text-destructive" data-testid="fee-reconcile-error">
          {error}
        </div>
      )}

      {/* Summary tiles */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="fee-reconcile-summary">
          <Tile label="Sessions" value={summary.sessions_total} sub={`${summary.sessions_fees_charged} charged`} testId="recon-stat-sessions" />
          <Tile label="Expected" value={fmtAmt(summary.total_expected)} testId="recon-stat-expected" />
          <Tile label="Collected" value={fmtAmt(summary.total_collected)} tone="good" testId="recon-stat-collected" />
          <Tile
            label="Gap"
            value={fmtAmt(totalGap)}
            tone={totalGap > 0 ? 'bad' : 'good'}
            sub={summary.members_with_outstanding ? `${summary.members_with_outstanding} member(s)` : 'all settled'}
            testId="recon-stat-gap"
          />
        </div>
      )}

      {loading && !data && (
        <div className="rounded-2xl border-2 border-border bg-card p-6 text-center text-xs text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
        </div>
      )}

      {/* Per-session table */}
      {data?.per_session?.length > 0 && (
        <section className="rounded-2xl border-2 border-border bg-card overflow-hidden" data-testid="fee-reconcile-sessions">
          <header className="px-4 py-3 border-b border-border bg-muted/30 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-primary" />
            <h3 className="font-display text-sm font-bold uppercase tracking-[0.16em] flex-1">By session</h3>
            <span className="text-xs text-muted-foreground">{data.per_session.length} row(s)</span>
          </header>
          <div className="divide-y divide-border">
            {data.per_session.map((s) => {
              const st = STATUS_PILLS[s.status] || STATUS_PILLS.uncharged;
              return (
                <button
                  key={s.session_id}
                  type="button"
                  onClick={() => navigate(`/groups/${groupId}/sessions/${s.session_id}`)}
                  className="w-full px-4 py-3 flex items-start gap-3 text-sm text-left hover:bg-muted/40 transition-colors"
                  data-testid={`fee-reconcile-session-${s.session_id}`}
                >
                  <span className="shrink-0 text-xs font-semibold text-muted-foreground w-12">
                    {fmtDate(s.date)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate flex items-center gap-2">
                      {s.title}
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs uppercase tracking-wider font-bold border ${st.cls}`}>
                        {st.label}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground tabular-nums">
                      {s.status === 'complimentary' ? (
                        <>{s.present_count} present + {s.guest_count} guest(s) · fees waived — free session</>
                      ) : (
                        <>{s.present_count} present + {s.guest_count} guest(s) × {fmtAmt(s.fee_per_attendee)} = {fmtAmt(s.expected)}</>
                      )}
                      {s.gap > 0 && (
                        <span className="ml-2 text-amber-600 font-semibold">· gap {fmtAmt(s.gap)}</span>
                      )}
                    </p>
                  </div>
                  <span className="shrink-0 text-right">
                    <p className="text-sm font-bold tabular-nums text-emerald-600">{fmtAmt(s.collected)}</p>
                    <p className="text-xs text-muted-foreground tabular-nums">of {fmtAmt(s.expected)}</p>
                  </span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-1" />
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Per-member table */}
      {data?.per_member?.length > 0 && (
        <section className="rounded-2xl border-2 border-border bg-card overflow-hidden" data-testid="fee-reconcile-members">
          <header className="px-4 py-3 border-b border-border bg-muted/30 flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            <h3 className="font-display text-sm font-bold uppercase tracking-[0.16em] flex-1">By member</h3>
            <span className="text-xs text-muted-foreground">{data.per_member.length} member(s)</span>
          </header>
          <div className="divide-y divide-border">
            {data.per_member.map((m) => (
              <div
                key={m.user_id}
                className="px-4 py-3 flex items-center gap-3 text-sm"
                data-testid={`fee-reconcile-member-${m.user_id}`}
              >
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">
                  {(m.name || '?').slice(0, 1).toUpperCase()}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{m.name}</p>
                  <p className="text-xs text-muted-foreground tabular-nums">
                    {m.sessions_paid}/{m.sessions_attended} session(s) paid
                    {m.sessions_unpaid_count > 0 && (
                      <span className="ml-1.5 text-amber-600 font-semibold">· {m.sessions_unpaid_count} unpaid</span>
                    )}
                  </p>
                </div>
                <span className={`shrink-0 text-sm font-bold tabular-nums ${m.outstanding > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                  {m.outstanding > 0 ? fmtAmt(m.outstanding) : <CheckCircle2 className="w-4 h-4" />}
                </span>
                {m.outstanding > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onWhatsAppNudge(m)}
                    disabled={!m.phone}
                    title={m.phone ? 'WhatsApp nudge' : 'No phone on file'}
                    data-testid={`fee-reconcile-nudge-${m.user_id}`}
                    className="h-7 px-2 gap-1 text-green-600 hover:text-green-700 disabled:opacity-30"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {!loading && data && data.per_session.length === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
          <Coins className="w-8 h-8 mx-auto mb-2 opacity-40" />
          No completed sessions in this period.
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, sub, tone = 'default', testId }) {
  const tones = {
    default: 'border-border bg-card',
    good: 'border-emerald-500/40 bg-emerald-500/5',
    bad: 'border-amber-500/40 bg-amber-500/5',
  };
  return (
    <div className={`rounded-xl border-2 px-3 py-2 ${tones[tone]}`} data-testid={testId}>
      <p className="text-xs uppercase tracking-[0.16em] font-bold text-muted-foreground">{label}</p>
      <p className="font-display text-base font-bold tabular-nums leading-tight mt-1">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}
